/**
 * NeuraHelper AI Agent – Chat Widget
 *
 * A floating chat bubble + slide-in panel that overlays every authenticated
 * page of the Neuracraft application.  Uses the existing Gemini service on the
 * frontend as a fallback when the backend agent endpoint is not reachable.
 *
 * Features:
 * - Role-aware greeting (reads user role from Firebase)
 * - Conversation history kept for the session
 * - Confirmation dialogs for sensitive actions
 * - Typing indicator while waiting for response
 * - Input validation + prompt-injection warning
 * - Responsive: works on mobile and desktop
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { auth, firestore } from "../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { API_BASE_URL } from "../../utils/apiBase";
import { MessageCircle, X, Send, Loader2, AlertTriangle, Bot, RefreshCw } from "lucide-react";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AGENT_NAME = "NeuraHelper";
const MAX_INPUT_LENGTH = 2000;

/** Rough client-side injection heuristics (mirrors backend patterns). */
const INJECTION_PATTERNS = [
  /ignore\s+(all\s+)?(previous|above|prior|earlier)\s+instructions?/i,
  /disregard\s+(all\s+)?(previous|above|prior|earlier)\s+instructions?/i,
  /reveal\s+(your\s+)?system\s+prompt/i,
  /jailbreak/i,
  /DAN\s+mode/i,
  /act\s+as\s+(a|an|the)\s+/i,
  /you\s+are\s+now\s+(a|an|the)\s+/i,
];

const ROLE_GREETINGS = {
  Admin: `Hello! I'm ${AGENT_NAME}, your Neuracraft AI assistant. I can help you manage companies, review system health, and more.`,
  Company: `Hello! I'm ${AGENT_NAME}. I can help you manage farmers, invoices, payments, and your milk marketplace.`,
  Employee: `Hello! I'm ${AGENT_NAME}. I can help you manage farmer records, invoices, and marketplace operations.`,
  Farmer: `Hello! I'm ${AGENT_NAME}, your dairy farming assistant. Ask me about milk records, payments, or marketplace tips.`,
  Client: `Hello! I'm ${AGENT_NAME}. I can help you with orders, invoices, and finding products in the marketplace.`,
  default: `Hello! I'm ${AGENT_NAME}, your Neuracraft AI assistant. How can I help you today?`,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function detectInjection(text) {
  return INJECTION_PATTERNS.some((p) => p.test(text));
}

function formatMessageText(text) {
  // Render basic markdown-style bold (**text**) and line breaks
  return text
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br />");
}

// ---------------------------------------------------------------------------
// NeuraHelperWidget component
// ---------------------------------------------------------------------------

const NeuraHelperWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => {
    // Prefer the browser crypto API for a collision-resistant session ID.
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `session-${crypto.randomUUID()}`;
    }
    // Fallback: timestamp-only (not security-critical; used only for
    // grouping conversation turns in the backend audit log).
    return `session-${Date.now()}`;
  });
  const [userRole, setUserRole] = useState(null);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [error, setError] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to latest message
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (isOpen) scrollToBottom();
  }, [messages, isOpen, scrollToBottom]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Load user role and set greeting on mount
  useEffect(() => {
    const loadRole = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setUserRole("default");
        setMessages([
          {
            role: "assistant",
            text: ROLE_GREETINGS.default,
            id: "greeting",
          },
        ]);
        return;
      }
      try {
        const snap = await getDoc(doc(firestore, "users", currentUser.uid));
        const role = snap.exists() ? snap.data()?.accountType || "default" : "default";
        setUserRole(role);
        setMessages([
          {
            role: "assistant",
            text: ROLE_GREETINGS[role] || ROLE_GREETINGS.default,
            id: "greeting",
          },
        ]);
      } catch {
        setUserRole("default");
        setMessages([
          {
            role: "assistant",
            text: ROLE_GREETINGS.default,
            id: "greeting",
          },
        ]);
      }
    };
    loadRole();
  }, []);

  // ---------------------------------------------------------------------------
  // Send message logic
  // ---------------------------------------------------------------------------

  const appendMessage = useCallback((role, text, extra = {}) => {
    const id =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}`;
    setMessages((prev) => [...prev, { role, text, id, ...extra }]);
  }, []);

  const handleSend = useCallback(
    async (overrideText) => {
      const text = (overrideText ?? input).trim();
      if (!text || loading) return;

      setError(null);

      // Client-side injection check
      if (detectInjection(text)) {
        setError(
          "Your message contains patterns that are not allowed. Please rephrase your question."
        );
        return;
      }

      if (text.length > MAX_INPUT_LENGTH) {
        setError(`Message is too long. Maximum ${MAX_INPUT_LENGTH} characters.`);
        return;
      }

      appendMessage("user", text);
      if (!overrideText) setInput("");
      setLoading(true);
      setPendingConfirmation(null);

      try {
        // Build history for the backend (exclude greeting)
        const history = messages
          .filter((m) => m.id !== "greeting")
          .map((m) => ({ role: m.role, content: m.text }));

        const currentUser = auth.currentUser;
        const idToken = currentUser ? await currentUser.getIdToken() : null;

        const response = await fetch(`${API_BASE_URL}/api/agent/chat/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          },
          body: JSON.stringify({
            message: text,
            session_id: sessionId,
            conversation_history: history,
            context: {
              page: window.location.pathname,
            },
          }),
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.error || `Server error ${response.status}`);
        }

        const data = await response.json();
        appendMessage("assistant", data.response);

        // Handle confirmation requests
        if (data.requires_confirmation && data.confirmation_prompt) {
          setPendingConfirmation({
            prompt: data.confirmation_prompt,
            originalMessage: text,
          });
        }
      } catch (err) {
        const isAuthError = err.message?.includes("401") || err.message?.includes("Authentication");
        appendMessage(
          "assistant",
          isAuthError
            ? "Please log in to use NeuraHelper's full capabilities."
            : `⚠️ I'm having trouble responding right now. Please try again shortly.\n\n_(${err.message})_`
        );
      } finally {
        setLoading(false);
      }
    },
    [input, loading, messages, sessionId, appendMessage]
  );

  const handleConfirm = useCallback(() => {
    if (!pendingConfirmation) return;
    setPendingConfirmation(null);
    handleSend(`Yes, confirm: ${pendingConfirmation.originalMessage}`);
  }, [pendingConfirmation, handleSend]);

  const handleCancel = useCallback(() => {
    setPendingConfirmation(null);
    appendMessage("assistant", "Action cancelled. Let me know if you need anything else.");
  }, [appendMessage]);

  const handleReset = useCallback(() => {
    setMessages([
      {
        role: "assistant",
        text: ROLE_GREETINGS[userRole] || ROLE_GREETINGS.default,
        id: "greeting",
      },
    ]);
    setPendingConfirmation(null);
    setError(null);
    setInput("");
  }, [userRole]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      {/* Floating chat button */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        aria-label={isOpen ? "Close NeuraHelper" : "Open NeuraHelper"}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-green-600 text-white shadow-lg hover:bg-green-700 active:scale-95 transition-all flex items-center justify-center"
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div
          className="fixed bottom-24 right-6 z-50 w-80 sm:w-96 max-h-[70vh] flex flex-col bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
          role="dialog"
          aria-label="NeuraHelper chat"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-green-600 text-white">
            <div className="flex items-center gap-2">
              <Bot size={20} />
              <span className="font-semibold text-sm">{AGENT_NAME}</span>
              {userRole && userRole !== "default" && (
                <span className="text-xs bg-green-500 px-2 py-0.5 rounded-full opacity-90">
                  {userRole}
                </span>
              )}
            </div>
            <button
              onClick={handleReset}
              title="Start new conversation"
              aria-label="Reset conversation"
              className="opacity-75 hover:opacity-100 transition-opacity"
            >
              <RefreshCw size={16} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-green-600 text-white rounded-br-none"
                      : "bg-gray-100 text-gray-800 rounded-bl-none"
                  }`}
                  // We trust our own filter_output + markdown rendering
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: formatMessageText(msg.text) }}
                />
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-500 px-3 py-2 rounded-xl rounded-bl-none flex items-center gap-2 text-sm">
                  <Loader2 size={14} className="animate-spin" />
                  <span>NeuraHelper is thinking…</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Error banner */}
          {error && (
            <div className="mx-3 mb-1 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Confirmation dialog */}
          {pendingConfirmation && (
            <div className="mx-3 mb-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm">
              <p className="text-amber-800 mb-2 font-medium">⚠️ Confirmation Required</p>
              <p className="text-amber-700 mb-3 text-xs">{pendingConfirmation.prompt}</p>
              <div className="flex gap-2">
                <button
                  onClick={handleConfirm}
                  className="flex-1 bg-green-600 text-white py-1.5 rounded-lg text-xs font-medium hover:bg-green-700 transition-colors"
                >
                  Confirm
                </button>
                <button
                  onClick={handleCancel}
                  className="flex-1 bg-gray-200 text-gray-700 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="px-3 pb-3 pt-1 border-t border-gray-100">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  if (error) setError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Ask NeuraHelper…"
                maxLength={MAX_INPUT_LENGTH}
                disabled={loading}
                className="flex-1 text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50"
                aria-label="Chat input"
              />
              <button
                onClick={() => handleSend()}
                disabled={loading || !input.trim()}
                aria-label="Send message"
                className="bg-green-600 text-white rounded-xl px-3 py-2 hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NeuraHelperWidget;
